import { MessageStatus, MessageType } from '@atlas/types'
import { z } from 'zod'

// ─── Public-facing schema (create / update inputs) ────────────────────────────

// Locale code from the `locale` classification axis. Axis codes use an
// underscore separator (e.g. 'es_es', 'us_en'); a hyphen ('es-es') is also
// accepted. Kept permissive: two letters, separator, two letters (either order).
const languageIso = z.string().regex(/^[a-z]{2}[-_][a-z]{2}$/i, 'languageIso must be a locale code, e.g. "es_es"')

export const messageCreateSchema = z.object({
	title: z.string().min(1),
	description: z.string().optional(),
	// Empty is valid: adding a target locale to a translation group creates a
	// pending, not-yet-translated placeholder row (content filled in later,
	// manually or by the AI translation flow) — the New/Edit message FORM
	// still requires non-empty content client-side before it lets a user submit.
	// Capped (not unbounded): this is short marketing copy, not a document —
	// a very large value multiplies per-locale Claude token cost on every
	// translation request against it.
	content: z.string().max(10_000, 'Content is too long (max 10,000 chars)'),
	status: z.enum(MessageStatus).default(MessageStatus.DRAFT),
	languageIso,
	messageType: z.enum(MessageType),
	// Placement within the piece — carousel card index ('1'), a video timestamp
	// ('00:05'), or 'single'. Free-text; the channel/template owns the semantics.
	position: z.string().min(1).optional(),
	// Optional: when creating a translation, pass the source's group id. Omitted
	// for a brand-new source message (the service mints a group + marks isSource).
	translationGroupId: z.uuid().optional(),
	isSource: z.boolean().optional(),
})

export const messageUpdateSchema = messageCreateSchema.partial()

/**
 * Request an AI translation of a source message into one or more target
 * locales (POST /messages/{id}/translations). Each locale creates (or reuses)
 * a target-locale row in the source's translation group.
 */
export const messageRequestTranslationSchema = z.object({
	// Capped (not unbounded): each locale fans out to its own Lambda invocation
	// and a billed Claude API call — an uncapped array is an unbounded-cost
	// vector for a single authorized request. 50 is generously above any
	// realistic real-world locale count for one message.
	targetLocales: z.array(languageIso).min(1).max(50, 'Cannot request more than 50 locales in one call'),
})

/** Each translation-requested event is one `ai-router-worker` invocation and one billed Claude call. */
const MAX_BATCH_TRANSLATION_EVENTS = 100

/**
 * Request AI translation for many messages in one call (POST
 * /messages/translations/batch). Each item is its own `{ messageId,
 * targetLocales }` pair, processed independently server-side (design.md D4 of
 * `campaign-message-translations` — one item's failure never aborts the
 * others). `.max(20)` items and `.max(50)` locales per item bound each
 * dimension individually, but NOT their product — 20 items x 50 locales
 * would still let one authenticated request queue 1,000 translation-requested
 * events, and `ai-router-worker` has no reserved concurrency cap, so that
 * many events firing at once risks bursting into hundreds of concurrent
 * Claude calls (cost spike, or Lambda-concurrency exhaustion that starves
 * every other tenant's translation traffic). The `.refine` below bounds the
 * combined total directly, rather than relying on the two per-dimension caps
 * to keep their product small by coincidence.
 */
export const messageRequestTranslationBatchSchema = z.object({
	items: z
		.array(
			z.object({
				messageId: z.uuid(),
				targetLocales: z.array(languageIso).min(1).max(50, 'Cannot request more than 50 locales in one call'),
			}),
		)
		.min(1)
		.max(20, 'Cannot request translation for more than 20 messages in one call')
		.refine(
			(items) => items.reduce((sum, item) => sum + item.targetLocales.length, 0) <= MAX_BATCH_TRANSLATION_EVENTS,
			`Cannot request more than ${MAX_BATCH_TRANSLATION_EVENTS} total (message, locale) translations in one batch call`,
		),
})

/**
 * Import carousel copy from a spreadsheet (POST /work-orders/{id}/messages/import).
 * The `.xlsx` is uploaded through the standard presigned-S3 file flow first; the
 * import endpoint takes only the resulting `fileId` (NOT the bytes) and fetches
 * the object server-side. `languageIso` optionally overrides the order's locale
 * for every created message.
 */
export const messageImportSchema = z.object({
	fileId: z.uuid(),
	languageIso: languageIso.optional(),
})

export type MessageCreateInput = z.infer<typeof messageCreateSchema>
export type MessageUpdateInput = z.infer<typeof messageUpdateSchema>
export type MessageImportInput = z.infer<typeof messageImportSchema>
export type MessageRequestTranslationInput = z.infer<typeof messageRequestTranslationSchema>
export type MessageRequestTranslationBatchInput = z.infer<typeof messageRequestTranslationBatchSchema>
